import { useMemo, useState } from 'react';

import { useAppSelector } from 'redux/store';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import TableViewLayout from 'layouts/TableViewLayout';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

import type { FilterConfig } from 'api/shared/listConfig';
import type { PythonLibraryRow } from 'api/python_libraries/endpoints/getPythonLibraries';
import { fetchPythonLibraries } from '../../../../redux/slices/pythonLibrariesSlice';

const toActionEntity = (row: PythonLibraryRow): ActionEntity => ({ type: 'python_library', id: row.python_library_id, name: row.name });

// `filtersConfig` is empty on purpose: the backend read takes no parameters, so a filter offered here
// would silently return the unfiltered list. `totalItems` counts the rows in the store, not a server
// total.
//
// The paging and sorting controls, however, ARE offered and neither works.
//
// Paging never leaves page 1: `useAdvanceTable` sets `pagination.pageSize` but no `pageIndex`, and
// `AdvanceTableFooter.handlePageChange` only moves its own `currentPage` and calls `onPageChange` —
// nothing advances TanStack's page index. So the footer and the URL move, the refetch they trigger
// is discarded by this resource's parameterless read, and the same rows stay on screen. Invisible
// today because `defaultPageSize` is 100 and there are fewer libraries than that; past 100 the rest
// of the list is unreachable while the footer reads "101 to 200 of 250".
//
// Sorting is inert for a different reason: `generateColumns` puts a `customOnClick` on each header
// and `AdvanceTable` prefers it over TanStack's own sort handler, so client-side sorting never
// engages; the click routes through `updateSort` into the query payload, which the read discards.
// The arrow moves, the order does not, and the page refetches for nothing.
//
// Both belong to the useTableController rework, not here.

const ListPythonLibraries = () => {
  const { items: libraries, loading, error } = useAppSelector((state) => state.pythonLibraries);
  const userData = useAppSelector((state) => state.auth.userData);

  const pageName = 'python-libraries';
  const [showSidebar, setShowSidebar] = useState(false);

  const headerConfig = useMemo(
    () => [
      { header_key: 'name', label: 'Name' },
      { header_key: 'filename', label: 'Filename' },
      { header_key: 'description', label: 'Description' },
      { header_key: 'hash_short', label: 'Hash' },
      { header_key: 'created_at', label: 'Created' },
      { header_key: 'updated_at', label: 'Updated' },
    ],
    []
  );
  const filtersConfig: FilterConfig[] = [];

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    page,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: fetchPythonLibraries,
    columnFormats: { created_at: 'datetime', updated_at: 'datetime' },
    getFormattedRow: (row: PythonLibraryRow) => ({
      ...row,
      // `hash` is NOT NULL, so the old falsy guard only ever caught '' — which validation can also
      // produce from a drifted value. Keep that case blank rather than rendering a bare '...'.
      hash_short: row.hash === '' ? '' : `${row.hash.substring(0, 12)}...`,
    }),
    // The column shows a prefix; the whole hash is what a user compares, so it is the hover title.
    columnCells: { hash_short: (row: PythonLibraryRow & { hash_short: string }) => <span title={row.hash || undefined}>{row.hash_short}</span> },
  });

  const isLoading = loading.fetchPythonLibraries;

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: { row: { original: PythonLibraryRow } }) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh });
      return <ActionDropdown actions={actions} items={['edit', 'addToTransportPythonLibrary', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });
  const columns = [...baseColumns, folderColumn, actionsColumn];
  const formattedData = useMemo(() => getFormattedData(libraries || []), [libraries]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => String(row.python_library_id),
  });

  const totalItems = libraries?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / (limit || 1)));

  const selectionEntities = table.getSelectedRowModel().rows.map((r: { original: PythonLibraryRow }) => toActionEntity(r.original));
  const toolbarActions = getActions({ entities: selectionEntities, refresh });

  return (
    <div className="p-4 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <h4 className="mb-3 flex-shrink-0">Python Libraries</h4>
      <TableViewLayout
        filtersConfig={filtersConfig}
        filters={filters}
        pageName={pageName}
        columns={headerConfig.map((c) => ({
          key: c.header_key,
          label: c.label,
        }))}
        pageSize={limit}
        error={error}
        onFilterChange={setFilterAndSync}
        onResetFilters={resetFilters}
        onPageSizeChange={updateLimit}
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={updatePage}
        table={table}
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        onRefresh={refresh}
        isLoading={isLoading}
        isSilentRefresh={isSilentRefresh}
        toolbarButtons={
          <>
            <ActionButton actions={toolbarActions} action="createPythonLibrary" variant="primary" />
            <ActionButton
              actions={toolbarActions}
              action="addToTransportPythonLibrary"
              className="ms-2"
              variant="secondary"
              label="Add selected to transport"
            />
          </>
        }
      />
    </div>
  );
};

export default ListPythonLibraries;
